import { getDeviceType, maskData, sha256Encrypt } from '@tencent/oneid-utils';
import { AuthnApi as CommonAuthnApi } from 'common/api';
import {
  APP_EDITION_TYPE,
  AUTHENTICATION_METHOD,
  NOT_FOUND_ERROR,
  TEMPLATE_MAP,
  TEMPLATES,
  VERSION_POINT,
} from 'common/constants/Constants';
import { IAccount, IUserInfo } from 'common/constants/Types';
import { AppsApi } from 'common/http/bff';
import type { IAppsConfig } from 'common/http/bff/Apps';
import CommonAuthnApiNew from 'common/http/services/AuthnApi';
import { AdminApiV2 } from 'common/http/servicesV2';
import CommonAccountApiV3 from 'common/http/servicesV3/AccountApi';
import CommonRolesApiV3, { IRolesGeneralConfig } from 'common/http/servicesV3/RolesApi';
import i18n from 'common/locales';
import Utils from 'common/utils';
import { APP_REPORT_KEY, dataReport as beaconReport } from 'common/utils/dataReport';
import JSRuntime from 'common/utils/JSRuntime';
import { VendorApi } from 'idpBase/api';
import { AuthnApi } from 'idpBase/http';
import { TGlobalStore, TUpdateStore } from 'idpBase/store';
import { DEFAULT_SETTINGS } from './constant';

class RootStore {
  public readonly global: TGlobalStore;
  public updateStore: TUpdateStore<RootStore>;
  public resetStore: () => void;
  public annotationOverrides = {
    isJustBrandDemoPC: false,
  };
  /** 用户信息 */
  public userInfo = {
    accountUser: {},
    user: {},
    authenticationMethod: undefined,
    personStatus: '',
  } as IUserInfo;
  /** 应用信息 */
  public appsInfo: Record<string, IAppsConfig> = {};
  /** 企业信息 */
  public account = {} as IAccount;
  /** 用户所属角色列表 */
  public joinRoles: { role_id: string }[] = [];
  /** 角色通用配置 */
  public rolesGeneralConfig: IRolesGeneralConfig = {
    service_code_role_belong: {},
    role_belong_admin_name: {},
    service_code_role_id: {},
    enable_create_role_role_belongs: [],
    enable_edit_manage_scope_role_ids: [],
  };
  // 租户的配置信息，包含订阅的应用、企业信息，自定义外观等
  public settings = DEFAULT_SETTINGS; // 数据请求在 /login 登录后以及 /sso_login 登录前
  public hasFetchSettings = false; // 是否获取过 settings
  public accountId = ''; // 有登录态或者自定义域名或者私有化才有值，不是始终有值
  public externalAppInfo = {
    logoUrl: '',
    name: '',
    redirectUri: '',
    id: '',
    edition: APP_EDITION_TYPE.COMPANY,
  };
  public externalAppType = '';

  public prevUrl = document.referrer; // 路由切换的前一个路由，给埋点数据上报使用
  public currentUrl = window.location.href; // 当前路由

  /** 版本type状态 */
  public definitionStatus: Record<VERSION_POINT, boolean>;

  /**
   * 获取当前组织名称
   */
  get orgName() {
    return JSRuntime.isTencentDoc ? i18n.t('组织', { ns: 'idp' }) : i18n.t('企业', { ns: 'idp' });
  }

  /** 是否超级管理员 */
  get isSuperAdmin() {
    return this.joinRoles.some(
      (role) => role.role_id === this.rolesGeneralConfig.service_code_role_id.OneID,
    );
  }

  /** 是否为管理员 */
  public get isAdmin() {
    return this.joinRoles.length > 0;
  }

  // admin系统外观参数,初始值
  public demoParams = {
    isFullScreen: !JSRuntime.isBrandDemoOrigin,
    isPc: !!JSRuntime.isBrandDemoOrigin,
  };

  public get isJustBrandDemoPC(): string {
    return (
      JSRuntime.isBrandDemoOrigin &&
      !m.get(this.demoParams, 'isFullScreen') &&
      m.get(this.demoParams, 'isPc')
    );
  }

  public get isDocsTemplate(): boolean {
    return m.get(this.settings, 'account.brands.template') === TEMPLATES.DOC;
  }

  get phoneCustomName() {
    return i18n.t('手机号', { ns: 'idp' });
  }

  private canRedirect = true;

  public matchDomain(domain: string) {
    const { host } = window.location;
    const configUrl = new URL(JSRuntime.originalIdpSite);
    let currentDomain = _.replace(host, configUrl.host, '');
    currentDomain = _.trimEnd(currentDomain, '.');
    return currentDomain === domain;
  }

  public async getUserInfo() {
    const res = await CommonAuthnApiNew.getSelfV3({ app: JSRuntime.appFromUrl });
    const { errCode, data: userInfo } = res;

    this.dataReport('e#oneid_total#all#get_self', { from_module: 'idp' });

    // 企业不存在
    if (errCode === NOT_FOUND_ERROR) {
      Utils.redirectToOriginalIdp();
      return;
    }

    // 其它的错误场景，如登录态过期
    if (errCode) {
      Utils.handleNoAuth();
      return;
    }

    window.localStorage.setItem('authenticationMethod', userInfo.authenticationMethod || '');

    this.updateStore({ userInfo });

    return userInfo;
  }

  /**
   * 获取企业信息
   */
  async getAccountInfo() {
    const account = await CommonAccountApiV3.getAccountInfo();
    this.updateStore({ account });
    return account;
  }

  /**
   * 获取用户所属角色列表
   */
  async getJoinRoles() {
    this.joinRoles = await CommonRolesApiV3.getJoinRoles();
    return this.joinRoles;
  }

  /**
   * 获取角色通用配置
   */
  async getRolesGeneralConfig() {
    const data = await CommonRolesApiV3.getRolesGeneralConfig();
    this.updateStore({ rolesGeneralConfig: data });
    return data;
  }

  public getIDPRedirectUrl(qs?: string, path?: string): string {
    const method = window.localStorage.getItem('authenticationMethod');
    if (method === AUTHENTICATION_METHOD.ENTERPRISE) {
      return JSRuntime.getIdpSite(qs, path);
    }
    return JSRuntime.getOriginalIdpSite(qs, path);
  }

  // 获得第三方应用的配置信息
  public getExternalAppInfo(appType: string) {
    return VendorApi.getExternalAppInfoByType(appType)
      .then((res) => {
        this.updateStore({ externalAppInfo: res, externalAppType: appType });
        return res;
      })
      .catch((err) => {
        this.global.handleError(err);
      });
  }

  /**
   * 获得租户初始配置数据
   * @param params 企业id
   * @returns 初始配置数据
   */
  public async getSettings(params: { accountId: string }) {
    const settings = await AuthnApi.getSettings(params);
    if (settings.errCode) return;

    const brands = m.get(settings, 'account.brands');
    const template = m.get(settings, 'account.brands.template');
    const styleInfo = m.get(TEMPLATE_MAP, template);
    const curBrands = _.mergeWith(brands, styleInfo, (tar: string, src: string) => tar || src);
    _.set(settings, 'account.brands', curBrands);
    this.updateStore({
      settings,
      hasFetchSettings: true,
    });
    return settings;
  }

  public setSettingsBrands(brands: any) {
    mobx.set(this.settings.account, 'brands', brands);
  }
  /**
   * idp 登录侧所有登录成功后的跳转都走这里
   * (暂不处理个人版账号情况)
   * */
  public async redirectToLandingPage(options?: {
    accountId?: string;
    username?: string;
    skipSelect?: string; // 去开通会议/文档需要跳过选择账号
  }) {
    const opts = options || {};
    // TODO: 未开通文档应用，需要跳转到无权限页面

    // 1. 地址栏有回调地址 redirectUrl 则登录成功后优先跳转
    const redirectUrl = JSRuntime.thirdLandingPage;

    if (redirectUrl) {
      // 多次跳转时，触发鉴权跳转，会导致多次鉴权产生报错。
      // 这里逻辑会前往redirectUrl，返回该页面，状态会重置。暂不手动恢复该状态
      if (!this.canRedirect) return;
      this.canRedirect = false;
      const isWhiteList = Utils.isWhiteRedirect(redirectUrl);
      // 不在白名单中不跳转 直接去portal
      if (!isWhiteList) return Utils.redirectToPortal();
      const url = JSRuntime.isPrivate
        ? redirectUrl
        : JSRuntime.getOAuth2RedirectUrl(redirectUrl, opts.username || 'username', true);
      const redirect = opts?.skipSelect ? JSRuntime.getSubscribeUrl(url, opts?.skipSelect) : url;
      if (redirect) {
        Utils.safeRedirect(redirect);
        Utils.postPopupRedirectMsg(redirect);
      }
      return;
    }

    // 2. 仅有一个套件应用且仅有一个vendorType=KIT的sso应用那么会直接触发这个 sso 应用的登录而不是跳转到 portal 页
    let settings = this.settings;
    if (!this.hasFetchSettings) {
      let accountId = opts.accountId || this.account.accountId;
      if (!accountId) {
        // 没有传 accountId 的动态获取一下
        const account = await CommonAccountApiV3.getAccountInfo();
        accountId = account.accountId;
      }

      accountId && (settings = await this.getSettings({ accountId }));
    }

    const suiteApps = m.get(settings, 'account.apps');

    if (!m.isArray(suiteApps) || suiteApps.length !== 1) return Utils.redirectToPortal();

    const res = await AuthnApi.getSSOApps();

    if (res.errCode) {
      return Utils.redirectToPortal();
    }

    const ssoApps = m.get(res, 'apps');

    if (
      m.isArray(ssoApps) &&
      ssoApps.length === 1 &&
      m.get(ssoApps, '0.vendorType') === 'KIT' &&
      m.get(ssoApps, '0.idpInitiated')
    ) {
      const appId = m.get(ssoApps, '0.id');
      const redirectUrl = Utils.getSSOLoginLink(appId);
      return Utils.safeRedirect(redirectUrl);
    }

    return Utils.redirectToPortal();
  }

  // 仅有自定义域名及私有化环境可以通过如下方式获得 settings
  public async initSettings() {
    const domainName = window.location.hostname;

    let accountId = this.accountId;
    const hash = Utils.getPureHashPath();
    if (JSRuntime.isCustomDomain && (!accountId || hash.includes('#/sso_login/identity'))) {
      // 自定义域名通过当前域名获得 AccountId
      const res = await AuthnApi.loginByEmailOrDomain({ domainName });
      if (res.errCode) return;
      accountId = _.get(res, 'accountId');
    }

    if (!accountId) return;

    await this.getSettings({ accountId });
    this.updateStore({ accountId });
  }

  // 重置accountId、settings等信息
  public resetAccount() {
    this.updateStore({
      accountId: '',
      settings: DEFAULT_SETTINGS,
      hasFetchSettings: false,
    });
  }

  public updateDataReportUrl() {
    if (this.currentUrl === window.location.href) return;
    this.updateStore({
      prevUrl: this.currentUrl,
      currentUrl: window.location.href,
    });
  }

  /**
   * 获取数据上报公参，供滑块等公共层方法使用
   * @returns 公参对象
   */
  public getReportGlobalParams(): Record<string, any> {
    const {
      accountUser: { aliasId, unionId, fuzzyMobile: accountUserMobile },
      user,
    } = this.userInfo;

    const { accountId, accountType, name, account_version } = this.account;

    const { fuzzyMobile: userMobile } = user || {};

    return {
      alias_id: aliasId,
      union_id: unionId,
      account_uid: accountId,
      account_type: accountType,
      account_name: maskData({ data: name }),
      prev_url: this.prevUrl || document.referrer || JSRuntime.thirdLandingPage, // NOTICE 在无登录态时，手动输入管理后台/工作台地址，会跳转到登录相关页面，prev_url是当前路径上的redirectUrl
      app_name: JSRuntime.appFromUrl,
      login_phone_number: sha256Encrypt(userMobile || accountUserMobile),
      account_version,
      role_ids: this.joinRoles.map((item) => item.role_id),
      environment_type: RUNTIME_LOCAL_ENV,
      from_module: 'idp',
      device_type: getDeviceType(),
    };
  }

  // 数据上报
  public dataReport(eventName: string, params?: Record<string, any>) {
    const { isSendBeacon, ...rest } = params || {};

    const reqParams = this.getReportGlobalParams();
    return beaconReport(eventName, reqParams, rest, {
      [APP_REPORT_KEY.WEMEET]: JSRuntime.isTencentMeeting,
      isSendBeacon,
    });
  }

  // 信息加密
  public async encryptRequest(params: { subject?: string; password: string }) {
    const [encryptErr, encrypt] = await Utils.resolvePromise(CommonAuthnApi.getPwdEncryptKey());
    if (encryptErr) return '';
    return Utils.encryptObj(encrypt.key, encrypt.kid, params);
  }

  /** 获取版本type信息 */
  public async getDefinitionStatus() {
    const definitionRet = await AdminApiV2.getAccountDefinition();
    if (definitionRet.errCode) return;
    const definitionStatus = definitionRet.account_definitions.reduce((pre, item) => {
      return { ...pre, [item.definition_type]: item.switch_option !== 'SwitchOff' };
    }, {}) as Record<VERSION_POINT, boolean>;
    this.updateStore({ definitionStatus });
  }

  /**
   * 获取应用信息
   */
  async getAppsInfo(appTypes: string[] = [JSRuntime.appFromUrl]) {
    const res = await AppsApi.getAppsInfo(appTypes);
    if (res.errCode) return {};
    this.appsInfo = res;
    return res;
  }
}

export default RootStore;
